import { useEffect, useState, type ReactElement, type ReactNode, type SVGProps } from 'react';
import { useIntl } from 'react-intl';
import { useLoaderData, type LoaderFunctionArgs } from 'react-router-dom';
import type {
  GetAdminOrganizationResponse,
  GetAdminPropertyResponse,
  PropertyCategory,
  PropertyStatus,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { Banner } from '@immo/ui-kit';
import { useDeletedByLabel } from '@/components/deleted-by';
import { DossierTab, useDossierController } from './dossier-tab';
import { InquiriesTab, useInquiriesController } from './inquiries-tab';
import { MemoryTab, useMemoryController } from './memory-tab';
import { BreadcrumbLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { DetailRow, Section, SidebarSection } from '@/components/detail-layout';
import {
  CalendarIcon,
  CheckSquareIcon,
  CommercialIcon,
  EuroIcon,
  GarageIcon,
  HeartIcon,
  InvestmentIcon,
  LandIcon,
  MembersIcon,
  OrganizationsIcon,
  PropertiesIcon,
  RulerIcon,
  TagIcon,
  WorkspacesIcon,
} from '@/components/layout/icons';

type PropertyDetailLoaderResult = {
  readonly property: GetAdminPropertyResponse;
  readonly organization: GetAdminOrganizationResponse | null;
};

export async function propertyDetailLoader({ params }: LoaderFunctionArgs): Promise<PropertyDetailLoaderResult> {
  const id = params['id'];

  if (typeof id !== 'string' || id.length === 0) {
    throw new Response('Missing property id', { status: 404 });
  }

  const propertyResult = await apiClient.adminProperties.get({ pathParams: { id } });

  if (!propertyResult.ok) {
    throw new Response('Failed to load property', { status: propertyResult.status });
  }

  const orgResult = await apiClient.adminOrganizations.get({ pathParams: { id: propertyResult.body.organizationId } });

  return {
    property: propertyResult.body,
    organization: orgResult.ok ? orgResult.body : null,
  };
}

const TYPE_ICON: Record<PropertyCategory, (props: SVGProps<SVGSVGElement>) => ReactElement> = {
  APARTMENT: PropertiesIcon,
  HOUSE: PropertiesIcon,
  COMMERCIAL: CommercialIcon,
  LAND: LandIcon,
  GARAGE_PARKING: GarageIcon,
};

type StatusTone = 'success' | 'neutral' | 'muted';

const STATUS_TONE: Record<PropertyStatus, StatusTone> = {
  ACTIVE: 'success',
  DRAFT: 'neutral',
  IN_ACQUISITION: 'neutral',
  INACTIVE: 'muted',
  ARCHIVED: 'muted',
};

const STATUS_CLASSES: Record<StatusTone, { wrap: string; dot: string }> = {
  success: { wrap: 'bg-success-50 text-success-700', dot: 'bg-success-500' },
  neutral: { wrap: 'bg-neutral-100 text-text-secondary', dot: 'bg-neutral-400' },
  muted: { wrap: 'bg-neutral-50 text-text-tertiary', dot: 'bg-neutral-300' },
};

const eurFormatter = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

const sqmFormatter = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });
const decimalFormatter = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 });

function formatEuro(amount: number | null): string | null {
  if (amount === null) return null;

  return eurFormatter.format(amount);
}

function formatSqm(value: number | null): string | null {
  if (value === null) return null;

  return `${sqmFormatter.format(value)} m²`;
}

function formatPricePerSqm(amount: number | null, area: number | null): string | null {
  if (amount === null || area === null || area === 0) return null;

  return eurFormatter.format(Math.round(amount / area));
}

function daysSince(iso: string): number {
  const diff = Date.now() - new Date(iso).getTime();

  return Math.max(0, Math.floor(diff / 86_400_000));
}

function shortId(id: string): string {
  return id.replace(/-/g, '').slice(-8).toUpperCase();
}

export function PropertyDetailPage() {
  const t = useTranslations('pages.admin.properties.detail');
  const intl = useIntl();
  const deletedByLabel = useDeletedByLabel();
  const tList = useTranslations('pages.admin.properties');
  const tCategory = useTranslations('common.immobilientyp.category');
  const { property, organization } = useLoaderData() as PropertyDetailLoaderResult;
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  // Lazy: the dossier is requested only when its tab is first activated, so the
  // route loader and the other tabs never pay for it (IMM-549.2).
  const dossier = useDossierController(property.id);

  useEffect(() => {
    if (activeTab === 'dossier') dossier.open();
  }, [activeTab, dossier]);

  const memory = useMemoryController(property.id);

  useEffect(() => {
    if (activeTab === 'memory') memory.open();
  }, [activeTab, memory.open]);

  const inquiries = useInquiriesController(property.id);

  useEffect(() => {
    if (activeTab === 'inquiries') inquiries.open();
  }, [activeTab, inquiries.open]);

  const TypeIcon = TYPE_ICON[property.category];
  const tone = STATUS_CLASSES[STATUS_TONE[property.status]];
  const statusLabel = tList(`status.${property.status}`);
  const sinceDays = daysSince(property.createdAt);
  const sinceLabel =
    sinceDays === 1 ? t('cards.status-since-day') : t('cards.status-since-days', { days: String(sinceDays) });
  const askingPrice =
    property.marketingType === 'SALE'
      ? (property.pricing.sale?.purchasePrice ?? null)
      : (property.pricing.rental?.baseRent ?? null);
  const livingArea = property.areas.find((a) => a.type === 'LIVING_AREA')?.value ?? null;
  const roomCount = property.areas.find((a) => a.type === 'ROOMS')?.value ?? null;
  const formattedPrice = formatEuro(askingPrice);
  const formattedArea = formatSqm(livingArea);
  const formattedPricePerSqm = formatPricePerSqm(askingPrice, livingArea);
  const formattedRooms = roomCount !== null ? decimalFormatter.format(roomCount) : null;
  const addressLine = [
    [property.address.street, property.address.houseNumber].filter((p) => p !== null && p !== '').join(' '),
    [property.address.postalCode, property.address.city].filter((p) => p !== '').join(' '),
  ]
    .filter((p) => p !== '')
    .join(', ');

  return (
    <>
      <PageTopbar
        leading={
          <BreadcrumbLeading
            crumbs={[{ label: tList('title'), to: '/workspaces/properties' }, { label: property.title }]}
            icon={PropertiesIcon}
          />
        }
      />
      <div className="flex min-h-full flex-col">
        <div className="px-6 pt-6">
          <header className="flex items-start gap-4">
            <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-border bg-white text-text-tertiary">
              <TypeIcon className="h-6 w-6" aria-hidden />
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <h1 className="text-2xl font-bold tracking-tight text-text-primary">{property.title}</h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-base-sm text-text-tertiary">
                {addressLine !== '' && (
                  <span className="inline-flex items-center gap-1.5">
                    <PropertiesIcon className="h-4 w-4" aria-hidden />
                    {addressLine}
                  </span>
                )}
                {(formattedArea !== null || formattedRooms !== null) && (
                  <span className="inline-flex items-center gap-1.5">
                    <RulerIcon className="h-4 w-4" aria-hidden />
                    {[formattedArea, formattedRooms !== null ? `${formattedRooms} Zi.` : null]
                      .filter((p): p is string => p !== null)
                      .join(', ')}
                  </span>
                )}
                {formattedPrice !== null && (
                  <span className="inline-flex items-center gap-1.5">
                    <EuroIcon className="h-4 w-4" aria-hidden />
                    {formattedPrice}
                    {formattedPricePerSqm !== null &&
                      t('header-price-per-sqm-suffix', { amount: formattedPricePerSqm })}
                  </span>
                )}
                <StatusPill label={statusLabel} tone={tone} />
                <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-2.5 py-0.5 text-text-secondary">
                  <span className="h-1.5 w-1.5 rounded-full bg-neutral-400" aria-hidden />
                  {t('id-prefix')} #{shortId(property.id)}
                </span>
              </div>
            </div>
          </header>
          {property.deletedAt !== null && (
            <Banner
              variant="warning"
              className="mt-4"
              title={t('deleted.title')}
              description={t('deleted.body', {
                deletedAt: intl.formatDate(property.deletedAt, { dateStyle: 'short', timeStyle: 'short' }),
                deletedBy: deletedByLabel(property.deletedBy),
              })}
            />
          )}
        </div>
        <div className="mt-6 border-b border-border px-6">
          <Tabs active={activeTab} onChange={setActiveTab} t={t} />
        </div>
        <div className="grid flex-1 grid-cols-1 gap-x-6 px-6 lg:grid-cols-[1fr_320px]">
          <main className="space-y-6 pt-6 pb-6">
            {activeTab === 'dossier' ? (
              <DossierTab state={dossier.state} onRetry={dossier.retry} />
            ) : activeTab === 'memory' ? (
              <MemoryTab propertyId={property.id} state={memory.state} onRetry={memory.retry} />
            ) : activeTab === 'inquiries' ? (
              <InquiriesTab propertyId={property.id} state={inquiries.state} onRetry={inquiries.retry} />
            ) : activeTab !== 'overview' ? (
              <div className="rounded-md border border-dashed border-border bg-canvas px-4 py-10 text-center text-base-sm text-text-tertiary">
                {t('tab-empty', { tab: t(`tabs.${activeTab}`) })}
              </div>
            ) : (
              <>
                <Section title={t('sections.key-facts')} icon={InvestmentIcon}>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <KeyFactCard
                      icon={TagIcon}
                      label={t('cards.status-label')}
                      value={<StatusPill label={statusLabel} tone={tone} />}
                      sub={sinceLabel}
                    />
                    <KeyFactCard
                      icon={EuroIcon}
                      label={t('cards.asking-price-label')}
                      value={formattedPrice ?? t('empty')}
                      sub={formattedPricePerSqm !== null ? `${formattedPricePerSqm} / m²` : undefined}
                      emphasised
                    />
                    <KeyFactCard
                      icon={InvestmentIcon}
                      label={t('cards.expected-revenue-label')}
                      value={t('empty')}
                      sub={t('coming-soon')}
                    />
                    <KeyFactCard
                      icon={HeartIcon}
                      label={t('cards.match-score-label')}
                      value={t('empty')}
                      sub={t('coming-soon')}
                    />
                    <KeyFactCard
                      icon={CalendarIcon}
                      label={t('cards.last-activity-label')}
                      value={t('cards.last-activity-empty')}
                      sub={undefined}
                    />
                    <KeyFactCard
                      icon={MembersIcon}
                      label={t('cards.broker-team-label')}
                      value={t('cards.member-singular')}
                      sub={t('coming-soon')}
                    />
                  </div>
                </Section>

                <Section title={t('sections.pipeline')} icon={InvestmentIcon}>
                  <div className="flex flex-wrap items-center gap-2">
                    <PipelineStage label={t('pipeline.first-contact')} count={0} />
                    <PipelineStage label={t('pipeline.viewing')} count={0} />
                    <PipelineStage label={t('pipeline.offer')} count={0} />
                    <PipelineStage label={t('pipeline.negotiation')} count={0} />
                    <PipelineStage label={t('pipeline.notary')} count={0} />
                  </div>
                </Section>

                <Section title={t('sections.activity')} icon={CalendarIcon}>
                  <div className="rounded-md border border-dashed border-border bg-canvas px-4 py-6 text-center text-base-sm text-text-tertiary">
                    {t('activity-empty')}
                  </div>
                </Section>

                <Section title={t('sections.tasks')} icon={CheckSquareIcon} subtitle={t('tasks-open', { count: '0' })}>
                  <div className="rounded-md border border-dashed border-border bg-canvas px-4 py-6 text-center text-base-sm text-text-tertiary">
                    {t('tasks-empty')}
                  </div>
                </Section>
              </>
            )}
          </main>

          <aside className="space-y-6 border-l border-border pl-6 pt-6">
            <SidebarSection title={t('sections.tenancy')}>
              <dl className="space-y-2 text-base-sm">
                <DetailRow
                  icon={WorkspacesIcon}
                  label={t('details.workspace-label')}
                  value={organization?.workspaceName ?? t('empty')}
                />
                <DetailRow
                  icon={OrganizationsIcon}
                  label={t('details.organization-label')}
                  value={organization?.name ?? t('empty')}
                />
              </dl>
            </SidebarSection>

            <SidebarSection title={t('sections.details')}>
              <dl className="space-y-2 text-base-sm">
                <DetailRow icon={TagIcon} label={t('details.type-label')} value={tCategory(property.category)} />
                <DetailRow
                  icon={PropertiesIcon}
                  label={t('details.location-label')}
                  value={property.address.city || t('empty')}
                />
                <DetailRow
                  icon={RulerIcon}
                  label={t('details.living-area-label')}
                  value={formattedArea ?? t('empty')}
                />
                <DetailRow icon={RulerIcon} label={t('details.rooms-label')} value={formattedRooms ?? t('empty')} />
                <DetailRow icon={CalendarIcon} label={t('details.year-built-label')} value={t('empty')} />
                <DetailRow icon={InvestmentIcon} label={t('details.energy-label')} value={t('empty')} />
                <DetailRow icon={EuroIcon} label={t('details.asking-label')} value={formattedPrice ?? t('empty')} />
                <DetailRow icon={EuroIcon} label={t('details.expected-label')} value={t('empty')} />
              </dl>
              <button type="button" className="mt-2 text-base-sm text-interactive hover:underline focus:outline-none">
                {t('details.show-all-fields')}
              </button>
            </SidebarSection>

            <SidebarSection title={t('sections.owner')}>
              <p className="text-base-sm text-text-tertiary">{t('coming-soon')}</p>
            </SidebarSection>

            <SidebarSection title={t('sections.linked-deals')}>
              <p className="text-base-sm text-text-tertiary">{t('coming-soon')}</p>
            </SidebarSection>

            <SidebarSection title={t('sections.categories')}>
              <p className="text-base-sm text-text-tertiary">{t('coming-soon')}</p>
            </SidebarSection>
          </aside>
        </div>
      </div>
    </>
  );
}

type TabKey =
  | 'overview'
  | 'activity'
  | 'expose'
  | 'emails'
  | 'calls'
  | 'team'
  | 'notes'
  | 'appointments'
  | 'dossier'
  | 'memory'
  | 'inquiries';

const TAB_KEYS: ReadonlyArray<TabKey> = [
  'overview',
  'activity',
  'expose',
  'emails',
  'calls',
  'team',
  'notes',
  'appointments',
  'dossier',
  'memory',
  'inquiries',
];

const TAB_COUNTS: Partial<Record<TabKey, number>> = {
  activity: 0,
  emails: 0,
  notes: 0,
  appointments: 0,
};

type TabsProps = {
  readonly active: TabKey;
  readonly onChange: (key: TabKey) => void;
  readonly t: (key: string) => string;
};

function Tabs({ active, onChange, t }: TabsProps) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
      {TAB_KEYS.map((key) => {
        const selected = key === active;
        const count = TAB_COUNTS[key];
        const classes = [
          'relative inline-flex items-center gap-2 pb-2 text-base font-medium transition-colors focus:outline-none focus-visible:shadow-focus',
          selected ? 'text-text-primary' : 'text-text-tertiary hover:text-text-secondary',
        ].join(' ');

        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className={classes}
            aria-current={selected ? 'page' : undefined}
          >
            {t(`tabs.${key}`)}
            {count !== undefined && (
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-md bg-neutral-100 px-1.5 text-xs text-text-tertiary">
                {count}
              </span>
            )}
            {selected && <span aria-hidden className="absolute -bottom-px left-0 right-0 h-0.5 bg-text-primary" />}
          </button>
        );
      })}
    </div>
  );
}

type KeyFactCardProps = {
  readonly icon: (props: SVGProps<SVGSVGElement>) => ReactElement;
  readonly label: string;
  readonly value: ReactNode;
  readonly sub: string | undefined;
  readonly emphasised?: boolean;
};

function KeyFactCard({ icon: Icon, label, value, sub, emphasised = false }: KeyFactCardProps) {
  return (
    <div className="rounded-lg border border-border bg-white px-4 py-3">
      <div className="flex items-center gap-2 text-base-sm text-text-tertiary">
        <Icon className="h-4 w-4" aria-hidden />
        {label}
      </div>
      <div
        className={
          emphasised ? 'mt-2 text-lg font-semibold text-text-primary tabular-nums' : 'mt-2 text-base text-text-primary'
        }
      >
        {value}
      </div>
      {sub !== undefined && <div className="mt-1 text-base-sm text-text-tertiary">{sub}</div>}
    </div>
  );
}

type PipelineStageProps = {
  readonly label: string;
  readonly count: number;
};

function PipelineStage({ label, count }: PipelineStageProps) {
  return (
    <span className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-3 py-1.5 text-base-sm text-text-secondary">
      {label}
      <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-neutral-100 px-1.5 text-xs text-text-tertiary">
        {count}
      </span>
    </span>
  );
}

type StatusPillProps = {
  readonly label: string;
  readonly tone: { wrap: string; dot: string };
};

function StatusPill({ label, tone }: StatusPillProps) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-base-sm ${tone.wrap}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} aria-hidden />
      {label}
    </span>
  );
}
